import {
    forwardRef,
    ForwardRefRenderFunction,
    Ref,
    useEffect,
    useImperativeHandle,
    useState
} from 'react';
import {
    AccountsTableViewModel,
    useGetTableViewForOverriddenAccountsByPropertyIdAndVersionIdsLazyQuery,
    VersionType
} from "../../../../__generated__/generated_types";

import {AccountsTableView} from "../accounts-table-view/AccountsTableView";
import {useAccountsTableViewData} from "../accounts-table-view/hooks/useAccountsTableViewData";

interface OverriddenAccountsTableViewProps {
    onDataUpdate: () => void,
    budgetYear: number
}

export interface OverriddenAccountsTableViewRefObject {
    imperativeRefreshTableData: () => void
}

const OverriddenAccountsTableViewRenderer:
    ForwardRefRenderFunction<OverriddenAccountsTableViewRefObject, OverriddenAccountsTableViewProps> = (
    {onDataUpdate, budgetYear}: OverriddenAccountsTableViewProps,
    ref: Ref<OverriddenAccountsTableViewRefObject>
) => {
    const {
        isReady, propertyId, property, startReforecastMonthIndex, year,
        starringReforcastVersionId, starringBudgetVersionId,
        finTotalReforcastVersionId, finTotalBudgetVersionId
    } = useAccountsTableViewData();
    const [data, setData] = useState<AccountsTableViewModel[] | undefined>(undefined);

    const [
        fetchTableData,
        {data: tableData, loading: tableDataLoading, error: tableDataLoadingError}
    ] = useGetTableViewForOverriddenAccountsByPropertyIdAndVersionIdsLazyQuery({fetchPolicy: "network-only"});

    const refreshTableData = () => {
        if (isReady && propertyId && startReforecastMonthIndex !== undefined && year &&
            starringReforcastVersionId && starringBudgetVersionId &&
            finTotalReforcastVersionId && finTotalBudgetVersionId) {
            fetchTableData({
                variables: {
                    propertyId: propertyId,
                    reforecastStartMonthIndex: startReforecastMonthIndex,
                    starringMetaArgInput: [
                        {
                            versionId: starringReforcastVersionId,
                            type: VersionType.Reforecast
                        },
                        {
                            versionId: starringBudgetVersionId,
                            type: VersionType.Budget
                        }
                    ],
                    financialTotalValueArgInput: [
                        {
                            versionId: finTotalReforcastVersionId,
                            year: year,
                            type: VersionType.YearTotal
                        },
                        {
                            versionId: finTotalBudgetVersionId,
                            year: year + 1,
                            type: VersionType.Budget
                        }
                    ],
                    budgetYear: budgetYear
                }
            });
        }
    };

    useImperativeHandle(
        ref,
        () => ({
            imperativeRefreshTableData() {
                refreshTableData();
            }
        }),
        [isReady, propertyId, startReforecastMonthIndex, year,
            starringReforcastVersionId, starringBudgetVersionId,
            finTotalReforcastVersionId, finTotalBudgetVersionId],
    );

    useEffect(() => {
        refreshTableData();
    }, [isReady, propertyId, startReforecastMonthIndex, year,
        starringReforcastVersionId, starringBudgetVersionId,
        finTotalReforcastVersionId, finTotalBudgetVersionId]);

    useEffect(
        () => {
            if (tableDataLoading || tableDataLoadingError || !tableData) {
                return;
            }
            if (tableData.getTableViewForOverriddenAccountsByPropertyIdAndVersionIds) {
                setData(tableData.getTableViewForOverriddenAccountsByPropertyIdAndVersionIds);
            } else {
                setData([]);
            }
        }, [tableData, tableDataLoading, tableDataLoadingError]
    );

    return (
        <AccountsTableView
            headerText={"Overridden Accounts"}
            style={{marginBottom: "35px"}}
            property={property}
            tableData={data}
            onStarringUpdate={() => {
                refreshTableData();
                onDataUpdate();
            }}
            onNoteUpdate={() => {
                refreshTableData();
                onDataUpdate();
            }}
        />
    );
};

const OverriddenAccountsTableView = forwardRef(OverriddenAccountsTableViewRenderer);
export default OverriddenAccountsTableView;
